import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { StockDialog } from "@/components/store/StockDialog";

const STATUS_STYLE = {
  Adequate: "bg-[#ECFDF5] text-[#2E7D32] border-[#2E7D32]/30",
  Reorder: "bg-[#FFFBEB] text-[#B45309] border-[#B45309]/30",
  Critical: "bg-[#FEF2F2] text-[#C92A2A] border-[#C92A2A]/30",
  "Out of Stock": "bg-[#F3F4F6] text-[#374151] border-[#9CA3AF]/40",
  Inactive: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
};

const blank = {
  generic_name: "", section: "", subcategory: "", description: "", manufacturer: "",
  catalogue_no: "", preferred_vendor: "", stock_unit: "Bottle", pack_size: "",
  storage_condition: "Room Temperature", analyzer: "", lot_tracked: true,
  min_level: 0, reorder_level: 0, max_level: 0, unit_cost: "", location: "", remarks: "", active: true,
};

const LBL = "text-[10px] uppercase tracking-[0.1em] text-[#6B7280]";

export default function ItemMaster() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("");
  const [status, setStatus] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [stockItem, setStockItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/items", { params: { q, section, status } });
      setItems(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, [q, section, status]);

  useEffect(() => { api.get("/store/meta").then(({ data }) => setMeta(data))
    .catch((e) => console.warn("store meta unavailable:", e)); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const subcats = useMemo(() => (meta?.sections?.[form.section] || []), [meta, form.section]);

  const openNew = () => { setForm({ ...blank, section: Object.keys(meta?.sections || {})[0] || "" }); setEditing("new"); };
  const openEdit = (it) => {
    setForm({ ...blank, ...it, unit_cost: it.unit_cost ?? "" });
    setEditing(it.item_id);
  };

  const save = async () => {
    if (!form.generic_name.trim() || !form.section) return toast.error("Item name and section are required");
    setSaving(true);
    try {
      const payload = {
        ...form,
        min_level: Number(form.min_level) || 0, reorder_level: Number(form.reorder_level) || 0,
        max_level: Number(form.max_level) || 0,
        unit_cost: form.unit_cost === "" ? null : Number(form.unit_cost),
      };
      if (editing === "new") await api.post("/store/items", payload);
      else await api.put(`/store/items/${editing}`, payload);
      toast.success(editing === "new" ? "Item added to the master" : "Item updated");
      setEditing(null);
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const retire = async (it) => {
    if (!window.confirm(`Retire ${it.generic_name}? Its ledger history is kept.`)) return;
    try {
      await api.delete(`/store/items/${it.item_id}`);
      toast.success("Item retired");
      await load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  return (
    <div data-testid="item-master-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Item Master</h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Permanent internal codes stay tied to the generic item, specification and stock unit — never to the
            vendor. Stock on hand is calculated live from the transactions ledger.
          </p>
        </div>
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/store/items.csv`} data-testid="items-csv-btn"
            className="inline-flex items-center h-9 px-3 rounded-md border border-[#E5E7EB] bg-white text-xs hover:border-[#002FA7]">
            <Download className="h-3.5 w-3.5 mr-1.5 text-[#002FA7]" /> CSV
          </a>
          <Button onClick={openNew} data-testid="add-item-btn" className="bg-[#002FA7] hover:bg-[#00237C] text-white">
            <Plus className="h-4 w-4 mr-1.5" /> Add item
          </Button>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="item-search"
            placeholder="Search code, item, manufacturer or catalogue…" className="pl-9 bg-white" />
        </div>
        <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="item-section-filter"
          className="h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm">
          <option value="">All sections</option>
          {Object.keys(meta?.sections || {}).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="item-status-filter"
          className="h-10 rounded-md border border-[#E5E7EB] bg-white px-3 text-sm">
          <option value="">All stock status</option>
          {["Adequate", "Reorder", "Critical", "Out of Stock"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="mt-4 rounded-md border border-[#E5E7EB] bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="items-table">
          <thead className="bg-[#F9FAFB]">
            <tr className="text-left text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">
              <th className="px-4 py-2.5">Item</th>
              <th className="px-4 py-2.5">Section</th>
              <th className="px-4 py-2.5">Manufacturer</th>
              <th className="px-4 py-2.5 text-right">On hand</th>
              <th className="px-4 py-2.5">Policy</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6B7280]">Loading…</td></tr>}
            {!loading && items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6B7280]" data-testid="items-empty">
                No items match. Add one to start the sub-store.</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.item_id} className="hover:bg-[#F9FAFB]" data-testid={`item-row-${it.code}`}>
                <td className="px-4 py-2.5">
                  <p className="font-medium text-[#111827]">{it.generic_name}</p>
                  <p className="text-xs text-[#6B7280]">
                    {it.code} · {it.pack_size || "—"} · {it.storage_condition}
                    {it.analyzer ? ` · ${it.analyzer}` : ""}
                  </p>
                </td>
                <td className="px-4 py-2.5 text-xs">{it.section}<br /><span className="text-[#6B7280]">{it.subcategory}</span></td>
                <td className="px-4 py-2.5 text-xs">{it.manufacturer || "—"}</td>
                <td className="px-4 py-2.5 text-right" data-testid={`item-onhand-${it.code}`}>
                  <span className="font-semibold text-[#111827]">{it.on_hand}</span>
                  <span className="text-xs text-[#6B7280]"> {it.stock_unit}</span>
                  {it.expiring_lots?.length > 0 && (
                    <p className="text-[10px] text-[#B45309]">{it.expiring_lots.length} lot(s) expiring ≤90d</p>
                  )}
                  {it.expired_lots?.length > 0 && (
                    <p className="text-[10px] text-[#C92A2A]">{it.expired_lots.length} expired lot(s)</p>
                  )}
                </td>
                <td className="px-4 py-2.5 text-xs text-[#6B7280]">
                  Min {it.min_level} · Reorder {it.reorder_level}<br />Max {it.max_level}
                </td>
                <td className="px-4 py-2.5">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] ${STATUS_STYLE[it.stock_status] || ""}`}
                    data-testid={`item-status-${it.code}`}>{it.stock_status}</span>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex justify-end gap-1">
                    <Button variant="outline" size="sm" onClick={() => setStockItem(it)}
                      data-testid={`item-stock-${it.code}`}>Stock</Button>
                    <button onClick={() => openEdit(it)} data-testid={`item-edit-${it.code}`}
                      className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#002FA7]"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => retire(it)} data-testid={`item-retire-${it.code}`}
                      className="p-1.5 rounded hover:bg-[#F3F4F6] text-[#C92A2A]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">
              {editing === "new" ? "Add item" : `Edit — ${form.code || form.generic_name}`}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B7280]">
              Sections and subcategories follow a tertiary-centre haematology store. The internal code is
              generated automatically and never changes.
            </DialogDescription>
          </DialogHeader>

          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2">
              <Label className={LBL}>Generic name *</Label>
              <Input value={form.generic_name} onChange={set("generic_name")} className="mt-1" data-testid="item-name-input" />
            </div>
            <div>
              <Label className={LBL}>Internal code</Label>
              <Input value={form.code || "Auto-generated"} disabled className="mt-1" data-testid="item-code-input" />
            </div>
            <div>
              <Label className={LBL}>Section *</Label>
              <select value={form.section} onChange={(e) => setForm((p) => ({ ...p, section: e.target.value, subcategory: "" }))}
                className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid="item-section-input">
                <option value="">Select section</option>
                {Object.keys(meta?.sections || {}).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className={LBL}>Subcategory</Label>
              <select value={form.subcategory} onChange={set("subcategory")}
                className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid="item-subcategory-input">
                <option value="">Select subcategory</option>
                {subcats.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className={LBL}>Manufacturer</Label>
              <Input value={form.manufacturer} onChange={set("manufacturer")} className="mt-1" data-testid="item-manufacturer-input" />
            </div>
            <div>
              <Label className={LBL}>Catalogue / Ref no.</Label>
              <Input value={form.catalogue_no} onChange={set("catalogue_no")} className="mt-1" data-testid="item-catalogue-input" />
            </div>
            <div>
              <Label className={LBL}>Preferred vendor</Label>
              <Input value={form.preferred_vendor} onChange={set("preferred_vendor")} className="mt-1" data-testid="item-vendor-input" />
            </div>
            <div>
              <Label className={LBL}>Compatible analyzer</Label>
              <Input value={form.analyzer} onChange={set("analyzer")} className="mt-1" data-testid="item-analyzer-input" />
            </div>
            <div>
              <Label className={LBL}>Stock unit</Label>
              <select value={form.stock_unit} onChange={set("stock_unit")}
                className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid="item-unit-input">
                {(meta?.stock_units || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className={LBL}>Pack size</Label>
              <Input value={form.pack_size} onChange={set("pack_size")} placeholder="500 tests / 20 mL"
                className="mt-1" data-testid="item-pack-input" />
            </div>
            <div>
              <Label className={LBL}>Storage condition</Label>
              <select value={form.storage_condition} onChange={set("storage_condition")}
                className="mt-1 h-10 w-full rounded-md border border-[#E5E7EB] bg-white px-2 text-sm" data-testid="item-storage-input">
                {(meta?.storage || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className={LBL}>Minimum level</Label>
              <Input value={form.min_level} onChange={set("min_level")} className="mt-1" data-testid="item-min-input" />
            </div>
            <div>
              <Label className={LBL}>Reorder level</Label>
              <Input value={form.reorder_level} onChange={set("reorder_level")} className="mt-1" data-testid="item-reorder-input" />
            </div>
            <div>
              <Label className={LBL}>Maximum level</Label>
              <Input value={form.max_level} onChange={set("max_level")} className="mt-1" data-testid="item-max-input" />
            </div>
            <div>
              <Label className={LBL}>Unit cost</Label>
              <Input value={form.unit_cost} onChange={set("unit_cost")} className="mt-1" data-testid="item-cost-input" />
            </div>
            <div>
              <Label className={LBL}>Storage location</Label>
              <Input value={form.location} onChange={set("location")} className="mt-1" data-testid="item-location-input" />
            </div>
            <label className="flex items-center gap-2 text-xs text-[#374151] mt-6">
              <input type="checkbox" checked={!!form.lot_tracked} data-testid="item-lot-tracked"
                onChange={(e) => setForm((p) => ({ ...p, lot_tracked: e.target.checked }))} />
              Lot / batch tracked
            </label>
            <div className="sm:col-span-3">
              <Label className={LBL}>Standard description</Label>
              <Input value={form.description} onChange={set("description")} className="mt-1" data-testid="item-description-input" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="item-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="item-save"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StockDialog item={stockItem} meta={meta} onClose={() => setStockItem(null)} onSaved={load} />
    </div>
  );
}
